import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Plus } from 'lucide-react';
import { PageHeader, Stat, Stats, DataTable, FilterBar } from '../components/office';
import UnitField from '../components/stock/UnitField';
import { stockApi, suppliersApi, purchaseOrdersApi } from '@verlo/shared/services/api';
import { useAuth } from '@verlo/shared/contexts/AuthContext';
import { Button } from '@verlo/shared/components/ui/button';
import { Input } from '@verlo/shared/components/ui/input';
import { Label } from '@verlo/shared/components/ui/label';
import { Notice } from '@verlo/shared/components/ui/notice';
import { Signal } from '@verlo/shared/components/ui/signal';
import { Segmented } from '@verlo/shared/components/ui/segmented';
import { SearchField } from '@verlo/shared/components/ui/search-field';
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@verlo/shared/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@verlo/shared/components/ui/select';
import { useAuthoringSite } from '../contexts/SiteContext';
import { parseUnit, perUnit, qty } from '../lib/units';

// Stock levels (Back office sweep, Oct 2026). What you have against what you want on the
// shelf. What's below par goes to Orders in one tap ("Order these"), rounded to each
// supplier's pack. Quantities and par levels change in the row and save when you leave the
// box; everything else about an item is in its dialog, where the unit is picked, not typed.

const money = (v) => new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(v || 0);
const EMPTY = { name: '', category: '', quantity: '', unit: 'kg', par_level: '', min_quantity: '', supplier_id: '', cost_per_unit: '', pack_size: '1', order_code: '' };

const Field = ({ label, hint, children }) => (
  <div className="space-y-1.5">
    <Label className="text-[12.5px] text-ink-soft">{label}</Label>
    {children}
    {hint && <p className="text-[12px] text-ink-faint">{hint}</p>}
  </div>
);

// A number in a row that saves when you leave it (or press Enter), not on every keystroke.
// `required`: an emptied box is someone about to type, not a nought, so it puts the old
// number back (a quantity); without it, empty means none (a par level).
function InlineNumber({ value, onSave, unit, placeholder, testId, label, required = false }) {
  const [v, setV] = useState(value ?? '');
  useEffect(() => { setV(value ?? ''); }, [value]);
  const commit = () => {
    if (String(v) === String(value ?? '')) return;
    if (v === '' && required) { setV(value ?? ''); return; }
    const n = v === '' ? null : Number(v);
    if (n !== null && (!Number.isFinite(n) || n < 0)) { setV(value ?? ''); return; }
    onSave(n);
  };
  return (
    <div className="flex items-center gap-1.5">
      <Input type="number" min="0" step="any" value={v} placeholder={placeholder} onChange={(e) => setV(e.target.value)}
        onBlur={commit} onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
        className="h-8 w-20 font-mono tabular-nums" aria-label={label} data-testid={testId} />
      {unit && unit !== 'each' && <span className="text-[12.5px] text-ink-soft">{unit}</span>}
    </div>
  );
}

const InventoryPage = () => {
  const navigate = useNavigate();
  const authoring = useAuthoringSite();
  const { isAdmin } = useAuth();
  const admin = isAdmin();
  const [items, setItems] = useState(null);
  const [low, setLow] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [search, setSearch] = useState('');
  const [show, setShow] = useState('all');
  const [category, setCategory] = useState('all');
  const [editing, setEditing] = useState(null); // null | 'new' | item
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = () => Promise.all([
    stockApi.getAll(),
    purchaseOrdersApi.suggestions().catch(() => ({ data: null })),
  ]).then(([s, sug]) => { setItems(s.data || []); setLow(sug.data); })
    .catch(() => { setItems([]); toast.error("Couldn't load stock"); });
  useEffect(() => { load(); }, []);
  useEffect(() => { suppliersApi.getAll().then((r) => setSuppliers(r.data || [])).catch(() => setSuppliers([])); }, []);

  const supplierName = useMemo(() => Object.fromEntries(suppliers.map((s) => [s.id, s.name])), [suppliers]);
  const all = items || [];
  const belowPar = all.filter((i) => i.par_level > 0 && i.quantity < i.par_level);
  const lowStock = all.filter((i) => i.is_low_stock);
  const value = all.reduce((t, i) => t + (Number(i.quantity) || 0) * (Number(i.cost_per_unit) || 0), 0);
  const categories = [...new Set(all.map((i) => i.category).filter(Boolean))].sort();

  const rows = all.filter((i) => {
    if (show === 'below' && !(i.par_level > 0 && i.quantity < i.par_level)) return false;
    if (show === 'low' && !i.is_low_stock) return false;
    if (category !== 'all' && i.category !== category) return false;
    return !search.trim() || i.name.toLowerCase().includes(search.trim().toLowerCase());
  });

  const patchRow = async (item, patch, words) => {
    try {
      if ('quantity' in patch) await stockApi.updateQuantity(item.id, patch.quantity ?? 0);
      else await stockApi.patch(item.id, patch);
      setItems((cur) => cur.map((i) => (i.id === item.id ? { ...i, ...patch, is_low_stock: 'quantity' in patch ? (patch.quantity ?? 0) <= (i.min_quantity || 0) : i.is_low_stock } : i)));
      toast.success(words);
      purchaseOrdersApi.suggestions().then((r) => setLow(r.data)).catch(() => {});
    } catch { toast.error(`Couldn't save ${item.name}`); }
  };

  const open = (item) => {
    setEditing(item || 'new');
    setForm(item ? {
      ...EMPTY, ...Object.fromEntries(Object.keys(EMPTY).map((k) => [k, item[k] ?? EMPTY[k]])),
      cost_per_unit: item.cost_per_unit == null ? '' : Number(item.cost_per_unit).toFixed(2),
      supplier_id: item.supplier_id || '',
    } : EMPTY);
  };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    if (!form.name.trim()) { toast.error('Give it a name'); return; }
    if (!form.unit) { toast.error('Pick a unit'); return; }
    const body = {
      name: form.name.trim(), category: form.category.trim() || 'General', unit: form.unit,
      supplier_id: form.supplier_id || null,
      par_level: parseFloat(form.par_level) || 0, min_quantity: parseFloat(form.min_quantity) || 0,
      cost_per_unit: parseFloat(form.cost_per_unit) || 0, pack_size: Math.max(parseFloat(form.pack_size) || 1, 0.001),
      order_code: form.order_code.trim(),
    };
    setSaving(true);
    try {
      if (editing === 'new') { await stockApi.create({ ...body, quantity: parseFloat(form.quantity) || 0 }, authoring.site); toast.success(`${body.name} added`); }
      else { await stockApi.patch(editing.id, body); toast.success(`${body.name} saved`); }
      setEditing(null);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Couldn't save it"); } finally { setSaving(false); }
  };
  const remove = async () => {
    if (!window.confirm(`Delete ${editing.name}? Its counts and history go with it.`)) return;
    try { await stockApi.delete(editing.id); toast.success(`${editing.name} deleted`); setEditing(null); load(); } catch { toast.error("Couldn't delete it"); }
  };

  const columns = [
    { key: 'name', label: 'Item', sortable: true, className: 'min-w-[220px]', render: (i) => (
      <div className="min-w-0">
        <div className="font-medium text-ink">{i.name}</div>
        <div className="flex items-center gap-2 text-[12.5px] text-ink-faint">{i.category}
          {i.is_low_stock ? <Signal tone="stop" dot={false}>low</Signal> : i.par_level > 0 && i.quantity < i.par_level ? <Signal tone="warn" dot={false}>below par</Signal> : null}
        </div>
      </div>
    ) },
    { key: 'quantity', label: 'In stock', sortable: true, render: (i) => (admin
      ? <InlineNumber required value={i.quantity} unit={i.unit} label={`${i.name} in stock`} testId={`qty-input-${i.id}`} onSave={(n) => patchRow(i, { quantity: n ?? 0 }, `${i.name}: ${qty(n ?? 0, i.unit)}`)} />
      : <span className="font-mono tabular-nums">{qty(i.quantity, i.unit)}</span>) },
    { key: 'par_level', label: 'Par', sortable: true, render: (i) => (admin
      ? <InlineNumber value={i.par_level || ''} unit={i.unit} placeholder="Set" label={`${i.name} par level`} testId={`par-input-${i.id}`} onSave={(n) => patchRow(i, { par_level: n || 0 }, 'Par level updated')} />
      : <span className="font-mono tabular-nums text-ink-soft">{i.par_level ? qty(i.par_level, i.unit) : '—'}</span>) },
    { key: 'level', label: 'Against par', sortValue: (i) => (i.par_level ? i.quantity / i.par_level : 9), render: (i) => {
      if (!i.par_level) return <span className="text-[12.5px] text-ink-faint">no par set</span>;
      const pct = Math.min(100, Math.round((i.quantity / i.par_level) * 100));
      return (
        <div className="flex w-32 items-center gap-2">
          <div className="h-1.5 flex-1 rounded-full bg-hairline"><div className={`h-1.5 rounded-full ${i.is_low_stock ? 'bg-signal-stop-line' : pct < 100 ? 'bg-signal-warn-line' : 'bg-signal-ok-line'}`} style={{ width: `${pct}%` }} /></div>
          <span className="w-9 text-right font-mono text-[12px] tabular-nums text-ink-soft">{pct}%</span>
        </div>
      );
    } },
    { key: 'supplier', label: 'Supplier', muted: true, className: 'whitespace-nowrap', sortable: true, sortValue: (i) => supplierName[i.supplier_id] || '', render: (i) => supplierName[i.supplier_id] || <span className="text-ink-faint">—</span> },
    { key: 'cost_per_unit', label: 'Cost', align: 'right', mono: true, className: 'whitespace-nowrap', sortable: true, render: (i) => (
      <span>{money(i.cost_per_unit)}<span className="text-[12px] text-ink-faint"> / {perUnit(i.unit)}</span></span>
    ) },
  ];

  const lowItems = low ? low.items - ((low.groups || []).find((g) => !g.supplier_id)?.lines.length || 0) : 0;

  return (
    <div className="space-y-5" data-testid="inventory-page">
      <PageHeader
        title="Stock levels"
        purpose="What you have, against what you want on the shelf."
        actions={admin && (
          <Button className="bg-verlo hover:bg-verlo-hover" onClick={() => open(null)} disabled={!authoring.ready} title={authoring.reason || undefined} data-testid="add-stock-item-btn">
            <Plus size={16} className="mr-2" />Add item
          </Button>
        )}
      />

      <Stats>
        <Stat label="Items" value={items ? all.length : null} />
        <Stat label="Below par" value={items ? belowPar.length : null} delta={belowPar.length ? { text: 'To order', tone: 'warn' } : undefined} />
        <Stat label="Low" value={items ? lowStock.length : null} delta={lowStock.length ? { text: 'At or under the low mark', tone: 'stop' } : undefined} />
        <Stat label="Stock value" value={items ? money(value) : null} hint="At what you last paid" />
      </Stats>

      {lowItems > 0 && (
        <Notice tone="warn" word="Order" title={`${lowItems} item${lowItems === 1 ? ' is' : 's are'} below par`} data-testid="reorder-suggestions-card"
          action={<Button className="bg-verlo hover:bg-verlo-hover" onClick={() => navigate('/back-office/orders')} data-testid="stock-order-these">Order these</Button>}>
          Bringing them up to par comes to <b className="font-mono">{money(low.total)}</b>, rounded to each supplier's pack size. Order these makes one draft per supplier for you to check and send.
        </Notice>
      )}

      <FilterBar right={<span>{rows.length} of {all.length}</span>}>
        <SearchField className="w-64" placeholder="Search stock…" aria-label="Search stock" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="inventory-search" />
        <Segmented value={show} onChange={setShow} data-testid="stock-show" options={[
          { value: 'all', label: 'All' }, { value: 'below', label: 'Below par' }, { value: 'low', label: 'Low' },
        ]} />
        {/* What the two mean (Chris's review L8). */}
        <span className="text-[12px] text-ink-faint" data-testid="stock-show-explained">
          {show === 'low' ? 'Low: at or under its low mark, about to run out.' : show === 'below' ? 'Below par: under what you keep, so it wants ordering.' : null}
        </span>
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger className="w-44" data-testid="category-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Every category</SelectItem>
            {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
      </FilterBar>

      <DataTable
        columns={columns} rows={rows} rowKey="id" loading={items === null} scroll
        defaultSort={{ key: 'name', dir: 'asc' }}
        rowTestId={(i) => `stock-row-${i.id}`}
        actions={admin ? (i) => <Button variant="ghost" size="sm" className="h-8" onClick={() => open(i)} data-testid={`edit-stock-${i.id}`}>Edit</Button> : undefined}
        empty={all.length
          ? { word: 'Stock', title: 'Nothing matches', children: 'Try another search, or show everything.', action: <Button variant="outline" onClick={() => { setSearch(''); setShow('all'); setCategory('all'); }}>Show everything</Button> }
          : { word: 'Stock', title: 'No stock items yet', children: 'Add what you keep on the shelf, with a par level, and Verlo tells you what to order.' }}
        testId="stock-table"
      />

      <Dialog open={editing !== null} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent size="form">
          <DialogHeader>
            <DialogTitle>{editing === 'new' ? 'Add a stock item' : form.name || 'Stock item'}</DialogTitle>
            <DialogDescription>{editing === 'new' ? 'What it is, how you count it, and who you buy it from.' : 'Change the count in the table; everything else is here.'}</DialogDescription>
          </DialogHeader>
          <DialogBody className="space-y-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field label="Name"><Input value={form.name} onChange={set('name')} autoFocus data-testid="stock-item-name" /></Field>
              <Field label="Category"><Input value={form.category} onChange={set('category')} placeholder="e.g. Dairy, meat, dry goods…" data-testid="stock-item-category" /></Field>
            </div>
            <Field label="Counted in"><UnitField value={form.unit} onChange={(unit) => setForm({ ...form, unit })} /></Field>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              {editing === 'new' && <Field label="In stock now"><Input type="number" min="0" step="any" value={form.quantity} onChange={set('quantity')} className="font-mono" data-testid="stock-item-quantity" /></Field>}
              <Field label="Par level" hint="What you want on the shelf"><Input type="number" min="0" step="any" value={form.par_level} onChange={set('par_level')} className="font-mono" data-testid="stock-item-par" /></Field>
              <Field label="Low at" hint="Flag it red at this"><Input type="number" min="0" step="any" value={form.min_quantity} onChange={set('min_quantity')} className="font-mono" data-testid="stock-item-threshold" /></Field>
            </div>
            <div className="rounded-lg border border-hairline p-4">
              <p className="mb-3 font-heading text-[11px] font-bold uppercase tracking-[0.08em] text-ink-faint">Buying it</p>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Field label="Supplier">
                  <Select value={form.supplier_id || 'none'} onValueChange={(v) => setForm({ ...form, supplier_id: v === 'none' ? '' : v })}>
                    <SelectTrigger data-testid="stock-item-supplier"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No supplier</SelectItem>
                      {suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label={`Cost per ${form.unit === 'each' ? 'one' : form.unit || 'unit'} (£)`}><Input type="number" min="0" step="0.01" value={form.cost_per_unit} onChange={set('cost_per_unit')} className="font-mono" data-testid="stock-item-cost" /></Field>
                <Field label="Their pack" hint={`How many ${form.unit === 'each' ? '' : `${parseUnit(form.unit).base === 'case' ? 'cases' : form.unit} `}in one of theirs`}><Input type="number" min="0" step="any" value={form.pack_size} onChange={set('pack_size')} className="font-mono" data-testid="stock-item-pack" /></Field>
                <Field label="Their code"><Input value={form.order_code} onChange={set('order_code')} maxLength={40} placeholder="Optional" data-testid="stock-item-code" /></Field>
              </div>
            </div>
          </DialogBody>
          <DialogFooter className="sm:justify-between">
            {editing !== 'new' ? <Button variant="ghost" className="text-signal-stop hover:text-signal-stop" onClick={remove}>Delete</Button> : <span />}
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
              <Button className="bg-verlo hover:bg-verlo-hover" onClick={save} disabled={saving} data-testid="save-stock-item-btn">{editing === 'new' ? 'Add item' : 'Save'}</Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InventoryPage;
